import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/features/_shared/queries';
import { toast } from '@/lib/toast';

type RuleType =
  | 'no_reports_received'
  | 'new_source_ip'
  | 'fail_ratio'
  | 'policy_downgrade'
  | 'volume_anomaly'
  | 'dns_record_changed';

const RULE_LABEL: Record<RuleType, string> = {
  no_reports_received: 'Geen DMARC-rapporten ontvangen',
  new_source_ip: 'Nieuwe verzendbron',
  fail_ratio: 'Te hoge faalratio',
  policy_downgrade: 'Beleid verlaagd in DNS',
  volume_anomaly: 'Volume-anomalie',
  dns_record_changed: 'DNS-record gewijzigd',
};

const REQUIRED: Record<RuleType, Array<{ key: string; label: string; placeholder: string }>> = {
  no_reports_received: [
    { key: 'silence_hours', label: 'Stilte (uren)', placeholder: '72' },
  ],
  new_source_ip: [],
  fail_ratio: [
    { key: 'threshold_pct', label: 'Drempel (%)', placeholder: '5' },
    { key: 'window_hours', label: 'Venster (uren)', placeholder: '24' },
  ],
  policy_downgrade: [],
  volume_anomaly: [
    { key: 'window_hours', label: 'Venster (uren)', placeholder: '168' },
    { key: 'z_threshold', label: 'Z-score drempel', placeholder: '3' },
  ],
  dns_record_changed: [],
};

export function AlertRuleModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [ruleType, setRuleType] = useState<RuleType>('fail_ratio');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [notifyTeam, setNotifyTeam] = useState(true);
  const [notifyKlant, setNotifyKlant] = useState(false);

  const create = useMutation({
    mutationFn: () => {
      const numericConfig: Record<string, number> = {};
      for (const field of REQUIRED[ruleType]) {
        const v = config[field.key];
        if (v === undefined || v === '') {
          throw new Error(`Vul "${field.label}" in`);
        }
        const n = Number(v);
        if (Number.isNaN(n)) throw new Error(`"${field.label}" moet een getal zijn`);
        numericConfig[field.key] = n;
      }
      return apiPost(`/tenants/${slug}/alert-rules`, {
        rule_type: ruleType,
        config: numericConfig,
        notify_serso_team: notifyTeam,
        notify_klant: notifyKlant,
        enabled: true,
      });
    },
    onSuccess: () => {
      toast({ tone: 'success', message: 'Alert-regel aangemaakt' });
      qc.invalidateQueries({ queryKey: ['alerts', 'rules', slug] });
      onClose();
    },
    onError: (err: Error) => {
      toast({ tone: 'error', message: `Aanmaken mislukt: ${err.message}` });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-[15px] font-semibold text-slate-900">Nieuwe alert-regel</h2>
        <p className="mt-0.5 text-[12px] text-slate-500">Tenant: {slug}</p>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[12px] font-medium text-slate-700">Regeltype</span>
            <select
              value={ruleType}
              onChange={(e) => {
                setRuleType(e.target.value as RuleType);
                setConfig({});
              }}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]"
            >
              {(Object.keys(RULE_LABEL) as RuleType[]).map((t) => (
                <option key={t} value={t}>
                  {RULE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>

          {REQUIRED[ruleType].map((field) => (
            <label key={field.key} className="block">
              <span className="text-[12px] font-medium text-slate-700">{field.label}</span>
              <input
                type="number"
                inputMode="numeric"
                value={config[field.key] ?? ''}
                placeholder={field.placeholder}
                onChange={(e) => setConfig((prev) => ({ ...prev, [field.key]: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]"
              />
            </label>
          ))}

          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              checked={notifyTeam}
              onChange={(e) => setNotifyTeam(e.target.checked)}
            />
            SerSo-team notificeren
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              checked={notifyKlant}
              onChange={(e) => setNotifyKlant(e.target.checked)}
            />
            Klantcontact notificeren
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {create.isPending ? 'Opslaan…' : 'Aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}
